/**
 * `gamification-core` — proyección del `activity` de stockpilot a GameEvents.
 *
 * La gamificación se DERIVA del log de actividad (bucket `activity_recent`;
 * el log maestro `activity` es append-only por trigger
 * `activity_append_only_guard` — SIN hash-chain, ver mig `00052`): no añade
 * tablas ni muta el oplog. `applyEvents` es
 * puro y determinista → el mismo feed produce el mismo `PlayerState` (local en
 * cliente; recomputable en servidor para antifraude).
 *
 * Mapea filas de `activity_recent` (entity_type + action + patch) a los eventos
 * de dominio declarados en `config.ts`. Solo entidades REALMENTE trazadas por el
 * write-path (`tracked_fields`): `stock_move` (created, con `reason`) y
 * `stock_picking` (updated, campo `state`).
 */
import { type ActivityRow, parseActivityPatch } from "../activity/format";
import { applyEvents } from "./engine";
import { STOCKPILOT_EVENTS, stockpilotGamificationConfig } from "./config";
import { dayKeyUTC } from "./streaks";
import { initialState } from "./engine";
import type { GameEvent, PlayerState } from "./types";

/** Reason que `validateInventoryCountMutation` estampa en cada move de conteo. */
const COUNT_ADJUSTMENT_REASON = "count-adjustment";

/** `client_ts` (hora local honesta) si existe; si no, `server_ts`. */
function timestampOf(row: Pick<ActivityRow, "client_ts" | "server_ts">): number | null {
  const raw = row.client_ts ?? row.server_ts;
  if (!raw) {
    return null;
  }
  const t = Date.parse(raw);
  return Number.isNaN(t) ? null : t;
}

/** Valor `new` de un campo del patch (transición o snapshot de `created`). */
function fieldNewValue(row: Pick<ActivityRow, "patch">, field: string): string | null {
  const change = parseActivityPatch(row.patch)[field];
  if (!change) {
    return null;
  }
  const v = change.new;
  return typeof v === "string" ? v : null;
}

/**
 * Traduce el feed de `activity` a eventos de gamificación (orden ascendente por
 * tiempo, como exige el reducer de rachas). Filas irrelevantes se ignoran.
 */
export function deriveGameEvents(rows: readonly ActivityRow[]): GameEvent[] {
  const events: GameEvent[] = [];
  for (const row of rows) {
    const at = timestampOf(row);
    if (at === null) {
      continue;
    }
    const day = dayKeyUTC(at);
    const base = { at, day, meta: { activityId: row.id } };

    if (row.entity_type === "stock_move" && row.action === "created") {
      // Un move de conteo (reason count-adjustment) reconcilia una diferencia;
      // cualquier otro es un movimiento de inventario. Mutuamente excluyentes.
      if (fieldNewValue(row, "reason") === COUNT_ADJUSTMENT_REASON) {
        events.push({ type: STOCKPILOT_EVENTS.countAdjusted, ...base });
      } else {
        events.push({ type: STOCKPILOT_EVENTS.stockMoved, ...base });
      }
    } else if (
      row.entity_type === "stock_picking" &&
      row.action === "updated" &&
      fieldNewValue(row, "state") === "done"
    ) {
      events.push({ type: STOCKPILOT_EVENTS.pickingConfirmed, ...base });
    }
  }
  // El feed llega server_ts DESC; el reducer necesita orden cronológico ASC.
  return events.toSorted((a, b) => a.at - b.at);
}

/**
 * Estado de juego derivado del feed de actividad. Puro: mismo feed → mismo
 * estado. Retorna XP/nivel/rachas/badges/quests listos para la UI.
 */
export function deriveGameState(rows: readonly ActivityRow[]): PlayerState {
  const events = deriveGameEvents(rows);
  return applyEvents(initialState(), events, stockpilotGamificationConfig).state;
}
